import { md5 } from 'js-md5'

import { POSTGRES_COLUMN_LENGTH_LIMIT } from '../query/types.ts'
// 以下、既存のテーブルでテーブル名を壊したくなかったので維持する
const ignoreTables = new Set([
  'kintone_【法人】活動登録_他社同行者テーブル',
  'kintone_【法人】活動登録_ターゲットテーブル',
  'salesforce_engage_reports___engage__email__template___share',
])

type ByteLengthCalculator = (input: string) => number

/**
 * TextEncoderを引数として渡すと型を書く必要があってimport文でnode専用になってしまう。
 * モジュールレベルで保持するとずっとメモリに残るのが気になる。
 * そこで関数ファクトリーパターンを使用し、各関数内でローカルにTextEncoderを作成し、
 * クロージャで保持することで、型レベルでは汎用的な関数型を使いつつ、
 * TextEncoderの再利用も実現している。
 */
const createByteCalculator = (): ByteLengthCalculator => {
  const encoder = new TextEncoder()
  return (input: string) => encoder.encode(input).length
}

// UTF-8エンコーディングでのバイト長を計算（Node.js・ブラウザ環境で同じ結果）
const getByteLength = (input: string, calculator: ByteLengthCalculator): number => calculator(input)

// シンプルなハッシュ関数（Node.js・ブラウザ環境で同じ結果）
// djb2アルゴリズムを使用
// 外部がMD5に依存した実装になっているので現状では使えない
// const simpleHash = (input: string): string => {
//   // eslint-disable-next-line @typescript-eslint/no-misused-spread -- コードポイント単位で分割するため
//   const hash = [...input]
//     .map((char) => char.codePointAt(0) ?? 0)
//     .reduce((hash, char) => (hash << 5) + hash + char, 5381) // hash * 33 + char
//
//   // 符号なし32bit整数に変換し、16進数8桁の文字列にする
//   return (hash >>> 0).toString(16).padStart(8, '0')
// }

// maxByteになるまで、sの先頭から文字列を取得（コードポイント単位で安全に処理）
const extractHead = (s: string, maxByte: number, calculator: ByteLengthCalculator): string =>
  // コードポイント単位に分割し、reduceで先頭から積み上げる
  // eslint-disable-next-line @typescript-eslint/no-misused-spread -- コードポイント単位で分割するため
  [...s].reduce((current, char) => {
    const next = current + char
    return getByteLength(next, calculator) <= maxByte ? next : current
  }, '')

export const isIdentifiable = (s: string, maxByte?: number): boolean => {
  const calculateByteLength = createByteCalculator()
  const length = getByteLength(s, calculateByteLength)
  return length <= (maxByte ?? POSTGRES_COLUMN_LENGTH_LIMIT)
}

// 文字列sのmaxByteまでをidentifierとして使うとき、
// 文字列がuniqueになるように加工して返す
// （postgresが63バイト分以降の文字列を無視してしまうので、これを防ぐための対応）
export const makeIdentifiable = (s: string, maxByte?: number): string => {
  const calculateByteLength = createByteCalculator()

  if (ignoreTables.has(s)) {
    return s
  }

  if (getByteLength(s, calculateByteLength) <= (maxByte ?? POSTGRES_COLUMN_LENGTH_LIMIT)) {
    return s
  }

  // const hash = simpleHash(s) // 8文字
  const hash = md5.hex(s).slice(0, 8) // 外部がMD5に依存している
  const headPart = extractHead(s, 32, calculateByteLength) // 32でいいけどなんで32で固定値？
  return `${headPart}_${hash}` // 8文字分のハッシュで衝突する可能性は十分少ないという仮定
}
